import { Skeleton } from 'antd';
import { connect } from 'react-redux';
import styled from 'styled-components';

import {
  Button,
  ButtonGroup,
  IconButton,
  TrashIcon,
  Popconfirm,
} from '@frontend/legacy-components';

import {
  deleteWorkStep,
  fetchQuoteWorkflow,
  saveWorkStepToList,
} from './data/actions';
import { getIsFetchingWorkSteps, getWorkSteps } from './data/reducer';
import WorkStepsModal from './workStepsModal/WorkStepModal';
import { open } from './workStepsModal/data/actions';
import { MODES as WORK_STEPS_MODAL_MODES } from './workStepsModal/data/constants';

const EmptyContainer = styled.div`
  background: ${({ theme }) => theme.color.page.plateLight};
  border: solid 1px ${({ theme }) => theme.color.separator.border};
  padding: 3rem;
`;

const EmptyMessage = styled.p`
  text-align: center;
`;

const Header = styled.p`
  ${({ theme }) => theme.font.size.C}
  font-weight: ${({ theme }) => theme.font.weight.heavy};
  margin-bottom: 1rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;

  > div {
    margin-bottom: 1rem;
  }
`;

const WorkSteps = styled.ul`
  margin-bottom: 1rem;
`;

const WorkStepWrapper = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
  background: ${({ theme }) => theme.color.page.surface};
  border: 1px solid ${({ theme }) => theme.color.separator.divider};
  color: ${({ theme }) => theme.color.feature.primary};
  min-height: 50px;
  transition: 0.3s ease all;
  width: 100%;
  word-break: break-word;
  ${({ theme }) => theme.font.size.D}

  :not(:last-child) {
    border-bottom: 0;
  }

  :hover {
    background: ${({ theme }) => theme.color.interaction.surfaceHover};
  }

  ${(props) =>
    props.isReadOnly
      ? 'cursor: default;'
      : `cursor: pointer;
        &:hover {
          background: ${({ theme }) => theme.color.page.plateLight};
          border-left: 5px solid ${({ theme }) => theme.color.brand.secondary};
          color: #333;
        }
        &:active,
        &:focus {
          background: #eeeeee;
          text-decoration: none;
        }`}
`;

const connector = connect(
  (state, { isReadOnlyPermission }) => ({
    isFetchingWorkSteps: getIsFetchingWorkSteps(state),
    isReadOnly: isReadOnlyPermission,
    workSteps: getWorkSteps(state),
  }),
  {
    addWorkStep: saveWorkStepToList.add,
    deleteWorkStep: deleteWorkStep.request,
    editWorkStep: saveWorkStepToList.edit,
    fetchQuoteWorkflow: fetchQuoteWorkflow.request,
    openModal: open.request,
  },
);

const WorkStepsList = ({
  addWorkStep,
  deleteWorkStep,
  editWorkStep,
  fetchQuoteWorkflow,
  isFetchingWorkSteps,
  isReadOnly,
  openModal,
  workSteps,
}) => {
  return (
    <>
      <WorkStepsModal />
      <Header>Available Workflow Steps</Header>
      {isFetchingWorkSteps ? (
        <LoadingContainer>
          <Skeleton.Input active size="larrge" />
          <Skeleton.Input active size="larrge" />
          <Skeleton.Input active size="larrge" />
        </LoadingContainer>
      ) : (
        <WorkSteps>
          {workSteps
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((workStep) => (
              <WorkStepWrapper
                disabled={isReadOnly}
                isReadOnly={isReadOnly}
                key={workStep.id}
                onClick={() =>
                  openModal({
                    workStepId: workStep.id,
                    callBack: ({ workStep }) => {
                      editWorkStep({ workStep });
                      fetchQuoteWorkflow();
                    },
                    mode: WORK_STEPS_MODAL_MODES.EDIT,
                  })
                }
              >
                <p>{workStep.name}</p>
                {!isReadOnly && (
                  <Popconfirm
                    label="Are you sure you want to delete this workflow step? This action cannot be undone."
                    placement="top-start"
                    confirmProps={{
                      children: 'Delete step',
                      colorScheme: 'destructive',
                    }}
                    onConfirm={(e) => {
                      e.stopPropagation();
                      deleteWorkStep({ workStepId: workStep.id });
                    }}
                    onCancel={(e) => e.stopPropagation()}
                  >
                    <IconButton onClick={(e) => e.stopPropagation()}>
                      <TrashIcon />
                    </IconButton>
                  </Popconfirm>
                )}
              </WorkStepWrapper>
            ))}
          {workSteps.length == 0 && (
            <EmptyContainer>
              <EmptyMessage>No Work Steps</EmptyMessage>
            </EmptyContainer>
          )}
        </WorkSteps>
      )}
      <ButtonGroup>
        <Button
          size={'lg'}
          variant={'primary'}
          isDisabled={isFetchingWorkSteps || isReadOnly}
          onClick={() =>
            openModal({
              callBack: addWorkStep,
              mode: WORK_STEPS_MODAL_MODES.CREATE,
            })
          }
        >
          Add New Step
        </Button>
      </ButtonGroup>
    </>
  );
};

export default connector(WorkStepsList);
